import type { AnyDomainEvent, Centavos } from '@abastece/shared';
import { orderKey } from '@abastece/shared';
import { precioId, type ApplyResult, type Reader } from '../state.js';

export async function reducePrecio(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'lista_precio.creada': {
      const p = event.payload;
      return {
        effects: [
          {
            kind: 'listaPrecio.upsert',
            id: p.listaId,
            row: {
              id: p.listaId,
              nombre: p.nombre,
              esPredeterminada: p.esPredeterminada,
              orden: p.orden,
              activa: true,
              rev,
            },
          },
        ],
        rejections: [],
      };
    }

    case 'lista_precio.actualizada': {
      const p = event.payload;
      const existing = await read.listaPrecio(p.listaId);
      if (!existing) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La lista no existe' }] };
      }
      const patch: Record<string, unknown> = {};
      if (p.nombre !== undefined) patch['nombre'] = p.nombre;
      if (p.esPredeterminada !== undefined) patch['esPredeterminada'] = p.esPredeterminada;
      if (p.orden !== undefined) patch['orden'] = p.orden;
      return { effects: [{ kind: 'listaPrecio.patch', id: p.listaId, rev, patch }], rejections: [] };
    }

    case 'lista_precio.eliminada':
      /**
       * Deactivated rather than deleted. Historical sales point at the list that priced them,
       * and a report of last quarter that cannot name the tier it sold on is not a report.
       */
      return {
        effects: [
          { kind: 'listaPrecio.patch', id: event.payload.listaId, rev, patch: { activa: false } },
        ],
        rejections: [],
      };

    case 'precio.fijado': {
      const p = event.payload;
      const unidad = await read.unidadVenta(p.unidadVentaId);
      if (!unidad) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La unidad de venta no existe' }] };
      }
      const lista = await read.listaPrecio(p.listaId);
      if (!lista) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La lista de precios no existe' }] };
      }
      const id = precioId(p.unidadVentaId, p.listaId);
      return {
        effects: [
          {
            kind: 'precio.set',
            id,
            row: {
              id,
              unidadVentaId: p.unidadVentaId,
              listaId: p.listaId,
              precio: p.precio as Centavos,
              rev,
            },
          },
        ],
        rejections: [],
      };
    }

    default:
      return null;
  }
}
